
import React, { useState, useEffect } from 'react';
import { Save, Moon, Sun, RefreshCw, Settings as SettingsIcon } from 'lucide-react';

const Settings: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('reachai_theme') as 'light' | 'dark' || 'light';
    const savedInterval = localStorage.getItem('reachai_refresh_interval');
    
    setTheme(savedTheme);
    if (savedInterval) {
      setRefreshInterval(parseInt(savedInterval));
    }
    
    // Apply theme to document
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleSave = () => {
    localStorage.setItem('reachai_theme', theme);
    localStorage.setItem('reachai_refresh_interval', refreshInterval.toString());
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden transition-colors">
        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-xl">
              <SettingsIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">User Preferences</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Customize your dashboard experience.</p>
            </div>
          </div>
          <button 
            onClick={handleSave}
            className="flex items-center space-x-2 bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100 dark:shadow-none active:scale-[0.98]"
          >
            <Save className="w-4 h-4" />
            <span>{isSaved ? 'Saved!' : 'Save Changes'}</span>
          </button>
        </div>

        <div className="p-8 space-y-8">
          {/* Theme Toggle */}
          <div className="flex items-center justify-between pb-6 border-b border-slate-100 dark:border-slate-800">
            <div className="space-y-1">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center">
                {theme === 'dark' ? <Moon className="w-4 h-4 mr-2 text-slate-400 dark:text-slate-500" /> : <Sun className="w-4 h-4 mr-2 text-slate-400 dark:text-slate-500" />}
                Theme
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400">Choose between light and dark mode.</p>
            </div>
            <button
              onClick={handleThemeToggle}
              className="relative inline-flex items-center h-8 rounded-full w-16 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              style={{ backgroundColor: theme === 'dark' ? '#4f46e5' : '#cbd5e1' }}
            >
              <span
                className={`inline-block w-6 h-6 transform transition-transform bg-white rounded-full shadow-lg ${
                  theme === 'dark' ? 'translate-x-9' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Refresh Interval */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2 text-slate-400 dark:text-slate-500" />
                  Auto-Refresh Interval
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400">How often to refresh dashboard data.</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{refreshInterval}</span>
                <span className="text-sm text-slate-500 dark:text-slate-400">seconds</span>
              </div>
            </div>
            <input
              type="range"
              min="10"
              max="120"
              step="10"
              value={refreshInterval}
              onChange={e => setRefreshInterval(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-slate-400 dark:text-slate-500">
              <span>10s</span>
              <span>60s</span>
              <span>120s</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
